import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import GroupShapeDetail from './GroupShapeDetail';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import NewShapeDetail from './NewShapeDetail';
import {
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  createShape,
  setCreatingNewShape,
  setSelectedShapes,
  setNewShapeName,
  setSelectedShapeId,
  setShowPasteGeosModal,
} from 'store/actions';

const GroupShapes = ({
  classes,
  // props
  selectedGroup,
  selectedShapes,
  selectedCustomShape,
  creatingNewShape,
  newShapeName,
  // actions
  createShape,
  setCreatingNewShape,
  setSelectedShapes,
  setSelectedShapeId,
  setNewShapeName,
  setShowPasteGeosModal,
}) => {
  // == Constants ============================================

  const noCustomShapes = !selectedGroup || selectedGroup.shapes.length === 0;

  // == useState =============================================

  const [isExpanded, setIsExpanded] = useState(true);
  const [addShapeAnchorEl, setAddShapeAnchorEl] = useState(null);

  // == Event Handlers =======================================

  const openAddShapeMenu = (e) => {
    setAddShapeAnchorEl(e.currentTarget);
  };

  const closeAddShapeMenu = () => {
    setAddShapeAnchorEl(null);
  };

  const onCancel = () => {
    setCreatingNewShape(false);
    setNewShapeName('');
    setSelectedShapes([]);
    setSelectedShapeId(null);
    closeAddShapeMenu();
  };

  const onCreateShapeClick = () => {
    if (newShapeName.trim().length === 0) {
      alert('Please enter a name for the shape.');
      return;
    }

    const geoRequests = selectedShapes.map((shape) => ({
      geoId: shape.id,
      level: shape.level,
    }));

    const shape = {
      groupId: selectedGroup.groupId,
      name: newShapeName,
      geoRequests,
    };

    createShape(shape);
    setNewShapeName('');
    setSelectedShapes([]);
    setSelectedShapeId(null);
    closeAddShapeMenu();
  };

  const onPasteGeosModalClick = () => {
    closeAddShapeMenu();
    setShowPasteGeosModal(true);
  };

  // == Components ===========================================

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <IconButton
            className={classes.expandButton}
            style={{ transform: `rotate(${isExpanded ? '0' : '-90'}deg)` }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ExpandMoreIcon />
          </IconButton>
          <Typography variant='subtitle2' className={classes.name}>
            {creatingNewShape
              ? 'New Shape'
              : noCustomShapes
              ? 'Create a shape'
              : !selectedCustomShape
              ? 'Select a shape'
              : `Selected shape - ${selectedCustomShape.shapeName}`}
          </Typography>
        </div>
        <div className={classes.headerRight}>
          {!creatingNewShape ? (
            <>
              <Tooltip title='Add Shape'>
                <IconButton
                  disabled={selectedGroup?.wasPublished}
                  className={classes.menuButton}
                  onClick={openAddShapeMenu}
                >
                  <AddCircleIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id='addShapeMenu'
                anchorEl={addShapeAnchorEl}
                open={Boolean(addShapeAnchorEl)}
                onClose={closeAddShapeMenu}
                getContentAnchorEl={null}
                anchorOrigin={{
                  horizontal: 'right',
                  vertical: 'center',
                }}
              >
                <MenuItem dense onClick={() => setCreatingNewShape(true)}>
                  Select Shapes
                </MenuItem>
                <MenuItem dense onClick={onPasteGeosModalClick}>
                  Paste Geo IDs
                </MenuItem>
              </Menu>
            </>
          ) : (
            <div>
              <Tooltip title='Save Shape'>
                <span>
                  <IconButton
                    className={classes.menuButton}
                    disabled={!selectedShapes || selectedShapes.length === 0}
                    onClick={onCreateShapeClick}
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancel'>
                <IconButton className={classes.menuButton} onClick={onCancel}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <Collapse in={isExpanded}>
        <div className={classes.container}>
          {creatingNewShape ? (
            <NewShapeDetail />
          ) : noCustomShapes ? (
            <div className={classes.noShapesDiv}>
              <Typography className={classes.noShapesText} variant='body2'>
                Group contains no shapes
              </Typography>
            </div>
          ) : !selectedCustomShape ? (
            <div className={classes.noShapesDiv}>
              <Typography className={classes.noShapesText} variant='body2'>
                No shape selected
              </Typography>
            </div>
          ) : (
            <GroupShapeDetail />
          )}
        </div>
      </Collapse>
    </div>
  );
};

// == Styles =============================================

const styles = (theme) => ({
  container: {
    padding: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px',
    backgroundColor: '#cbcbcb',
    borderBottom: '1px solid #b7b7b7',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
  },
  noShapesDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px',
  },
  noShapesText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem',
  },
  menuButton: {
    padding: 2,
  },
});

// == Connect =============================================

const mapStateToProps = ({ assumptionGroupsState }) => ({
  selectedCustomShape: assumptionGroupsState.selectedCustomShape,
  selectedGroup: assumptionGroupsState.selectedGroup,
  selectedShapes: assumptionGroupsState.selectedShapes,
  creatingNewShape: assumptionGroupsState.creatingNewShape,
  newShapeName: assumptionGroupsState.newShapeName,
});

const mapDispatchToProps = {
  createShape,
  setCreatingNewShape,
  setSelectedShapes,
  setNewShapeName,
  setSelectedShapeId,
  setShowPasteGeosModal,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(GroupShapes);
